/**
 * Company Admin API Client
 * Handles all API calls to Company Admin endpoints with authentication
 */
import axios from 'axios';
import { API_BASE_URL } from '@/config/api';
import { CA_BASE } from '@/utils/getBasePath';

const API_BASE = `${API_BASE_URL}/api/company-admin`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token interceptor
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('company_admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('company_admin_token');
      window.location.href = CA_BASE || '/';
    }
    return Promise.reject(error);
  }
);

// ============ Auth ============
export const login = (email, password) => 
  api.post('/login', { email, password });

export const getProfile = () => 
  api.get('/profile');

// ============ Stats ============
export const getStats = () => 
  api.get('/stats');

// ============ Restaurants ============
export const getRestaurants = () => 
  api.get('/restaurants');

export const createRestaurant = (data) => 
  api.post('/restaurants', data);

export const updateRestaurant = (id, data) => 
  api.put(`/restaurants/${id}`, data);

export const deleteRestaurant = (id) => 
  api.delete(`/restaurants/${id}`);

// ============ Users ============
export const getUsers = () => 
  api.get('/users');

export const createUser = (data) => 
  api.post('/users', data);

export const updateUser = (id, data) => 
  api.put(`/users/${id}`, data);

export const deleteUser = (id) => 
  api.delete(`/users/${id}`);

export const resetUserPassword = (id) => 
  api.post(`/users/${id}/reset-password`);

// ============ Tickets ============
export const getTickets = (params = {}) => 
  api.get('/tickets', { params });

export const createTicket = (data) => 
  api.post('/tickets', data);

export const updateTicket = (id, data) => 
  api.put(`/tickets/${id}`, data);

export const getTicketMessages = (ticketId) => 
  api.get(`/tickets/${ticketId}/messages`);

export const addTicketMessage = (ticketId, message) => 
  api.post(`/tickets/${ticketId}/messages`, { message });

// ============ Announcements ============
export const getAnnouncements = () => 
  api.get('/announcements');

export const createAnnouncement = (data) => 
  api.post('/announcements', data);

export const updateAnnouncement = (id, data) => 
  api.put(`/announcements/${id}`, data);

export const publishAnnouncement = (id) => 
  api.post(`/announcements/${id}/publish`);

export const deleteAnnouncement = (id) => 
  api.delete(`/announcements/${id}`);

// ============ Feature Flags ============
export const getFeatureFlags = () => 
  api.get('/feature-flags');

export const createFeatureFlag = (data) => 
  api.post('/feature-flags', data);

export const setFlagDefault = (flagKey, defaultEnabled) => 
  api.put(`/feature-flags/${flagKey}/default`, { default_enabled: defaultEnabled });

export const setFlagOverride = (flagKey, restaurantId, enabled) => 
  api.put(`/feature-flags/${flagKey}/restaurants/${restaurantId}`, { enabled });

// ============ Settings ============
export const getSettings = (scope = 'GLOBAL', restaurantId = null) => {
  const params = { scope };
  if (restaurantId) params.restaurant_id = restaurantId;
  return api.get('/settings', { params });
};

export const upsertSetting = (data) => 
  api.put('/settings', data);

// ============ API Keys ============
export const getApiKeys = (restaurantId = null) => {
  const params = restaurantId ? { restaurant_id: restaurantId } : {};
  return api.get('/api-keys', { params });
};

export const createApiKey = (data) => 
  api.post('/api-keys', data);

export const revokeApiKey = (id) => 
  api.post(`/api-keys/${id}/revoke`);

// ============ Subscriptions ============
export const getSubscriptions = (params = {}) => 
  api.get('/subscriptions', { params });


// ============ Reports ============
export const createReportExport = (data) => 
  api.post('/reports/export', data);

export const getReportStatus = (id) => 
  api.get(`/reports/export/${id}`);

export const getReportDownload = (id) => 
  api.get(`/reports/export/${id}/download`);

// ============ Audit Logs ============
export const getAuditLogs = (params = {}) => 
  api.get('/audit-logs', { params });

// ============ RBAC ============
export const getRoles = () => 
  api.get('/roles');

export const createRole = (data) => 
  api.post('/roles', data);

export const updateRole = (id, data) => 
  api.put(`/roles/${id}`, data);

export const deleteRole = (id) => 
  api.delete(`/roles/${id}`);

export const getPermissions = () => 
  api.get('/permissions');

export const assignRolePermissions = (roleId, permissionIds) => 
  api.put(`/roles/${roleId}/permissions`, { permission_ids: permissionIds });

// ============ Transactions ============
export const getTransactions = () => 
  api.get('/transactions');

// ============ Analytics ============
export const getAnalytics = (period = '30d') => 
  api.get('/analytics', { params: { period } });

export default api;
